import { useRef, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  HardDriveDownload, Mail, Send, CloudUpload, RefreshCw, Clock,
  CheckCircle2, AlertCircle, Download, Save,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/admin/Fields";
import CardCollapse from "./CardCollapse";

const SCHEDULE_LABEL = {
  manual: "Manuel (yalnızca butonla)",
  daily: "Günlük (otomatik)",
  weekly: "Haftalık (otomatik)",
  monthly: "Aylık (otomatik)",
};
const RETENTION_OPTS = [[5, "Son 5 yedek"], [10, "Son 10 yedek"], [30, "Son 30 yedek"], [0, "Sınırsız"]];

function fmtBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, x = Number(n);
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 ? 2 : 1)} ${u[i]}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function BackupManager() {
  const qc = useQueryClient();
  const { data: status } = useQuery({
    queryKey: ["admin", "backup", "status"],
    queryFn: () => api.get("/admin/backup/status").then((r) => r.data),
    refetchInterval: 15000,
  });
  const { data: comm } = useQuery({
    queryKey: ["admin", "settings", "communication-status"],
    queryFn: () => api.get("/admin/settings").then((r) => r.data?.communication || {}),
  });
  const { data: list } = useQuery({
    queryKey: ["admin", "backup", "list"],
    queryFn: () => api.get("/admin/backup/list").then((r) => r.data),
    refetchInterval: 30000,
  });

  const [running, setRunning] = useState(false);
  const [backupEmail, setBackupEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    if (comm) setBackupEmail(comm.backup_email || comm.notify_email || "");
  }, [comm?.backup_email, comm?.notify_email]);

  const bchans = Array.isArray(comm?.backup_channels) ? comm.backup_channels : null;
  const emailAddr = (comm?.backup_email || comm?.notify_email || "").trim();
  const emailReady = !!emailAddr;
  const tgReady = !!(comm?.telegram_enabled && comm?.telegram_token && comm?.telegram_chat_id);
  const emailOn = bchans ? (bchans.includes("email") && emailReady) : emailReady;
  const tgOn = bchans ? (bchans.includes("telegram") && tgReady) : tgReady;
  const anyChannel = emailOn || tgOn;

  const saveBackupEmail = async () => {
    const val = backupEmail.trim();
    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { toast.error("Geçerli bir e-posta adresi girin"); return; }
    setSavingEmail(true);
    try {
      await api.put("/admin/settings/communication", { backup_email: val });
      toast.success(val ? "Yedek alıcı adresi kaydedildi" : "Yedek alıcı adresi temizlendi");
      qc.invalidateQueries({ queryKey: ["admin", "settings", "communication-status"] });
    } catch (e) { toast.error(errMsg(e)); }
    setSavingEmail(false);
  };

  const saveConfig = async (patch) => {
    try {
      await api.put("/admin/backup/config", patch);
      qc.invalidateQueries({ queryKey: ["admin", "backup", "status"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      const parts = [];
      if (data.last_backup_email === "sent") parts.push("E-posta ✓");
      if (data.last_backup_telegram === "sent") parts.push("Telegram ✓");
      parts.push(`${(data.last_backup_size / 1024 / 1024).toFixed(2)} MB`);
      toast.success(`Yedek alındı · ${parts.join(" · ")}`);
      qc.invalidateQueries({ queryKey: ["admin", "backup", "status"] });
      qc.invalidateQueries({ queryKey: ["admin", "backup", "list"] });
    } catch (e) { toast.error(errMsg(e)); }
    setRunning(false);
  };

  if (!status) return <div className="bg-white border rounded-xl p-5 text-sm text-stone-400">Yedek durumu yükleniyor…</div>;

  const chip = (
    <span className={`text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${anyChannel ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`} data-testid="backup-status-chip">
      {anyChannel ? "Hazır" : "Kanal seçili değil"}
    </span>
  );

  return (
    <CardCollapse
      testid="backup-manager"
      icon={<HardDriveDownload size={16} />}
      title="Site Yedekleme"
      description="Tüm site verileri (metinler, görseller, ayarlar, mesajlar) ZIP olarak paketlenir ve seçtiğiniz kanallara gönderilir."
      chip={chip}
    >
      {/* Channels overview */}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="backup-channels">
        <div className={`border rounded-lg p-3 flex gap-3 ${emailOn ? "border-emerald-200 bg-emerald-50/40" : "border-stone-200 bg-stone-50/60"}`}>
          <Mail size={20} className={emailOn ? "text-emerald-600" : "text-stone-400"} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium flex items-center gap-2">E-posta ile{emailOn ? <CheckCircle2 size={13} className="text-emerald-600" /> : <AlertCircle size={13} className="text-amber-600" />}</p>
            <p className="text-xs text-stone-600 mt-0.5">
              {emailOn
                ? <>Yedek <code className="bg-white border rounded px-1">{emailAddr}</code> adresine gider. 15 MB altında ekli, üstünde indirme linki.</>
                : !emailAddr
                  ? <>Aşağıdaki alana yedek alıcı e-posta adresinizi girin.</>
                  : <>“Yedekleme bildirimleri → E-posta” seçimini açın.</>
              }
            </p>
            <div className="mt-2 flex gap-2">
              <Input type="email" placeholder="yedek@orneksite.com" value={backupEmail} onChange={(e) => setBackupEmail(e.target.value)} className="h-9 text-sm" data-testid="backup-email-input" />
              <Button type="button" size="sm" variant="outline" onClick={saveBackupEmail} disabled={savingEmail || backupEmail === (comm?.backup_email || comm?.notify_email || "")} data-testid="backup-email-save"><Save size={13} className="mr-1" />Kaydet</Button>
            </div>
          </div>
        </div>
        <div className={`border rounded-lg p-3 flex gap-3 ${tgOn ? "border-emerald-200 bg-emerald-50/40" : "border-stone-200 bg-stone-50/60"}`}>
          <Send size={20} className={tgOn ? "text-emerald-600" : "text-stone-400"} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium flex items-center gap-2">Telegram ile{tgOn ? <CheckCircle2 size={13} className="text-emerald-600" /> : <AlertCircle size={13} className="text-amber-600" />}</p>
            <p className="text-xs text-stone-600 mt-0.5">
              {tgOn
                ? <>Yedek, bot sohbetine dosya olarak düşer. 20 MB üstünde gönderilmez.</>
                : !tgReady
                  ? <>İletişim ayarlarından bot tokenınızı ve sohbet ID’nizi girip Telegram kanalını açın.</>
                  : <>İletişim ayarlarında “Yedekleme bildirimleri → Telegram” seçimini açın.</>
              }
            </p>
          </div>
        </div>
      </div>
      {!anyChannel && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="backup-no-channel-warning">
          ⚠ Hiç kanal seçili değil. “İletişim ayarları” kartından E-posta veya Telegram’ı açın. Yedek ayrıca sunucuda da saklanır, aşağıda listeden indirebilirsiniz.
        </p>
      )}

      {/* Schedule / retention */}
      <div className="grid md:grid-cols-2 gap-3 border-t pt-4">
        <Field label="Periyot">
          <select value={status.schedule} onChange={(e) => saveConfig({ schedule: e.target.value })} data-testid="backup-schedule" className="h-10 border rounded-md px-3 text-sm w-full bg-white">
            {Object.entries(SCHEDULE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Saklama politikası" hint="Sunucuda saklanan yedek sayısı. Aşan eskiler otomatik silinir.">
          <select value={status.retention} onChange={(e) => saveConfig({ retention: parseInt(e.target.value, 10) })} data-testid="backup-retention" className="h-10 border rounded-md px-3 text-sm w-full bg-white">
            {RETENTION_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
      </div>

      {/* Last run + Run now */}
      <div className="flex items-center justify-between border-t pt-4 flex-wrap gap-3">
        <div className="grid sm:grid-cols-4 gap-2 text-xs flex-1 min-w-0">
          <div className="bg-stone-50 border rounded p-2">
            <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Son yedek</p>
            <p className="font-medium flex items-center gap-1"><Clock size={11} />{fmtDate(status.last_backup_at)}</p>
          </div>
          <div className="bg-stone-50 border rounded p-2">
            <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Boyut</p>
            <p className="font-medium">{fmtBytes(status.last_backup_size)}</p>
          </div>
          <div className="bg-stone-50 border rounded p-2">
            <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">E-posta</p>
            <p className={`font-medium ${status.last_backup_email === "sent" ? "text-emerald-700" : status.last_backup_email === "failed" ? "text-red-600" : "text-stone-500"}`}>
              {status.last_backup_email === "sent" ? "Gönderildi" : status.last_backup_email === "failed" ? "Hata" : status.last_backup_email === "skipped" ? "Atlandı" : "—"}
            </p>
          </div>
          <div className="bg-stone-50 border rounded p-2">
            <p className="text-stone-500 uppercase tracking-wide text-[10px] mb-0.5">Telegram</p>
            <p className={`font-medium ${status.last_backup_telegram === "sent" ? "text-emerald-700" : status.last_backup_telegram === "failed" ? "text-red-600" : "text-stone-500"}`}>
              {status.last_backup_telegram === "sent" ? "Gönderildi" : status.last_backup_telegram === "failed" ? "Hata" : status.last_backup_telegram === "skipped" ? "Atlandı" : "—"}
            </p>
          </div>
        </div>
        <Button type="button" onClick={runNow} disabled={running} data-testid="backup-run-now">
          {running ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <CloudUpload size={14} className="mr-1" />}
          {running ? "Yedekleniyor…" : "Şimdi yedekle"}
        </Button>
      </div>
      {status.last_backup_error && !/storageQuotaExceeded|Service Account/.test(status.last_backup_error) && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="backup-last-error">⚠ {status.last_backup_error}</p>}


      {/* Backup list */}
      {list?.files?.length > 0 && (
        <div className="border rounded-lg">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <p className="text-sm font-medium">Yedekler ({list.files.length})</p>
            <Button type="button" variant="ghost" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ["admin", "backup", "list"] })} data-testid="backup-list-refresh"><RefreshCw size={12} className="mr-1" />Yenile</Button>
          </div>
          <ul className="divide-y max-h-80 overflow-y-auto" data-testid="backup-file-list">
            {list.files.map((f) => (
              <li key={f.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate" title={f.name}>{f.name}</p>
                  <p className="text-xs text-stone-500">{fmtDate(f.createdTime)} · {fmtBytes(f.size)}</p>
                </div>
                {f.webViewLink && <a href={f.webViewLink} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 inline-flex items-center gap-1 underline shrink-0" data-testid={`backup-download-${f.id}`}><Download size={12} />İndir</a>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </CardCollapse>
  );
}
